import React, { useEffect, useRef, useState } from 'react'

import {
  Grid,
  Col,
  Heading2,
  Box,
  Body2,
  Color,
  Display,
  AlignItems,
  Space,
  Image,
  JustifyContent,
  Join,
  withCitrusTheme,
  BorderStyle,
  TextAlign,
} from '@gousto-internal/citrus-react'

import type { WelcomeDiscountBannerData, WelcomeHeaderData } from '@features/welcome-page'
import { DiscountBanner } from '@features/welcome-page'

import { ReferAFriend } from 'routes/OrderConfirmation/components/ReferAFriend'

import { AppPromoV2 } from '../AppPromoV2'
import { BuildYourNextBoxContainer } from '../BuildYourNextBox/BuildYourNextBoxContainer'
import { WhatsComingUpNext } from '../WhatsComingUpNext/WhatsComingUpNext'
import { YourFirstBox } from '../YourFirstBox'
import { getMenuByPeriodId } from '../api/getMenuByPeriodId'
import WelcomeGoustoBox from '../assets/Box_icon.svg'
import { useWelcomeTracking } from '../hooks/useWelcomeTracking'

const RoundedBox = withCitrusTheme(Box, () => ({
  borderRadius: 8,
}))

function renderMobileLayout(
  renderedOrderSummary: React.JSX.Element,
  renderedBuildYourNextBox: React.JSX.Element,
  renderedAppPromo: React.JSX.Element,
  renderReferAFriend: (
    renderWrapper: (inviteSection: React.ReactNode) => React.JSX.Element,
  ) => React.JSX.Element | null,
  header: WelcomeHeaderData,
  discountBanner: WelcomeDiscountBannerData | null,
) {
  return (
    <Col size={12} display={['block', 'block', 'none', 'none']}>
      <Col size={12}>
        <Box>
          <Box
            display={Display.Flex}
            alignItems={AlignItems.Center}
            justifyContent={JustifyContent.Center}
            paddingBottom={4}
          >
            <Image
              width="130px"
              height="96px"
              src={WelcomeGoustoBox}
              alt="Gousto logo"
              data-testid="welcome-gousto-box-image"
            />
          </Box>
          <Box
            display={Display.Flex}
            alignItems={AlignItems.Center}
            justifyContent={JustifyContent.Center}
          >
            <Heading2 size={9} textAlign={TextAlign.Center}>
              {header.title}
            </Heading2>
          </Box>
        </Box>
        <Space size={3} />
      </Col>
      <DiscountBanner discountBanner={discountBanner} />
      <Space size={6} />
      <Join with={<Space size={6} />}>
        <Box>{renderedOrderSummary}</Box>
        <RoundedBox
          bg={Color.White}
          paddingV={4}
          paddingH={3}
          borderWidth={0.5}
          borderStyle={BorderStyle.Solid}
          borderColor={Color.ColdGrey_100}
        >
          {renderedBuildYourNextBox}
        </RoundedBox>

        <RoundedBox
          borderWidth={0.5}
          borderStyle={BorderStyle.Solid}
          borderColor={Color.ColdGrey_100}
          bg={Color.White}
          display={Display.Flex}
          paddingV={4}
          paddingH={3}
          data-testing="appPromo"
          justifyContent={JustifyContent.Center}
        >
          {renderedAppPromo}
        </RoundedBox>
        {renderReferAFriend((inviteSection) => (
          <RoundedBox
            borderWidth={0.5}
            borderStyle={BorderStyle.Solid}
            borderColor={Color.ColdGrey_100}
            bg={Color.White}
            display={Display.Flex}
            paddingV={4}
            paddingH={3}
            data-testing="referAFriendSection"
          >
            {inviteSection}
          </RoundedBox>
        ))}
      </Join>
    </Col>
  )
}

function renderTabletAndDesktopLayout(
  renderedOrderSummary: React.JSX.Element,
  renderedBuildYourNextBox: React.JSX.Element,
  renderedAppPromo: React.JSX.Element,
  renderReferAFriend: (
    renderWrapper: (inviteSection: React.ReactNode) => React.JSX.Element,
  ) => React.JSX.Element | null,
  header: WelcomeHeaderData,
  discountBanner: WelcomeDiscountBannerData | null,
) {
  return (
    <>
      <Col size={12} display={['none', 'none', 'block', 'block']}>
        <Box>
          <Box
            display={Display.Flex}
            alignItems={AlignItems.Center}
            justifyContent={JustifyContent.Center}
            paddingBottom={4}
          >
            <Image
              width="178px"
              height="124px"
              src={WelcomeGoustoBox}
              alt="Gousto logo"
              data-testid="welcome-gousto-box-image"
            />
          </Box>
          <Heading2 size={9}>{header.title}</Heading2>
        </Box>
        <Space size={2} />
        <Box>
          <Body2 color={Color.ColdGrey_800}>{header.subtitle}</Body2>
        </Box>
      </Col>
      <Col size={7} display={['none', 'none', 'block', 'block']}>
        <Box>
          <DiscountBanner discountBanner={discountBanner} />
        </Box>
        <Space size={6} />
        <Join with={<Space size={6} />}>
          <RoundedBox
            bg={Color.White}
            paddingV={5}
            paddingH={3}
            borderWidth={0.5}
            borderStyle={BorderStyle.Solid}
            borderColor={Color.ColdGrey_100}
          >
            {renderedBuildYourNextBox}
          </RoundedBox>
          <RoundedBox
            borderWidth={0.5}
            borderStyle={BorderStyle.Solid}
            borderColor={Color.ColdGrey_100}
            bg={Color.White}
            paddingV={3}
            paddingH={3}
            display={['none', 'none', Display.Flex, Display.Flex]}
          >
            {renderedAppPromo}
          </RoundedBox>
          {renderReferAFriend((inviteSection) => (
            <RoundedBox
              borderWidth={0.5}
              borderStyle={BorderStyle.Solid}
              borderColor={Color.ColdGrey_100}
              bg={Color.White}
              paddingV={3}
              paddingH={3}
              display={['none', 'none', 'none', Display.Flex]}
              alignItems={AlignItems.Center}
              data-testing="referAFriendSection"
            >
              {inviteSection}
            </RoundedBox>
          ))}
          {renderReferAFriend((inviteSection) => (
            <RoundedBox
              borderWidth={0.5}
              borderStyle={BorderStyle.Solid}
              borderColor={Color.ColdGrey_100}
              bg={Color.White}
              paddingV={3}
              paddingH={3}
              display={['none', 'none', Display.Flex, 'none']}
              alignItems={AlignItems.Center}
            >
              {inviteSection}
            </RoundedBox>
          ))}
        </Join>
      </Col>
      <Col size={5} display={['none', 'none', 'block', 'block']}>
        <Join with={<Space size={6} />}>
          <Box>{renderedOrderSummary}</Box>
        </Join>
      </Col>
    </>
  )
}

interface WelcomeFirstBoxOrderWrapperProps {
  header: WelcomeHeaderData
  discountBanner: WelcomeDiscountBannerData | null
  periodId: string
  isSignUpSeamlessFlowIntoPlanningEnabled: boolean
  hasShareableOffer: boolean
}

export function WelcomeFirstBoxOrderWrapper({
  header,
  discountBanner,
  periodId,
  isSignUpSeamlessFlowIntoPlanningEnabled,
  hasShareableOffer,
}: WelcomeFirstBoxOrderWrapperProps) {
  const [tertiaryMenu, setTertiaryMenu] = useState<boolean>(false)
  const { trackWelcomePageLanded } = useWelcomeTracking()

  const hasTrackedPageChangeRef = useRef(false)

  useEffect(() => {
    let cancelledCall = false

    async function getUsersMenu() {
      const usersMenuFromPeriodId = await getMenuByPeriodId(periodId)
      if (cancelledCall) return

      setTertiaryMenu(usersMenuFromPeriodId === 'tertiary')

      if (!hasTrackedPageChangeRef.current && usersMenuFromPeriodId) {
        hasTrackedPageChangeRef.current = true
        trackWelcomePageLanded({
          firstOrderWeek: usersMenuFromPeriodId,
          newLocation: window.location.href,
        })
      }
    }

    getUsersMenu()

    return () => {
      cancelledCall = true
    }
  }, [periodId, trackWelcomePageLanded])

  const renderedOrderSummary = <YourFirstBox />

  const renderedBuildYourNextBox = tertiaryMenu ? (
    <WhatsComingUpNext />
  ) : (
    <BuildYourNextBoxContainer />
  )

  // Join spaces its children by element count, so the invite is omitted at this level for its
  // spacing to go with it.
  const renderReferAFriend = (
    renderWrapper: (inviteSection: React.ReactNode) => React.JSX.Element,
  ) =>
    hasShareableOffer ? (
      <ReferAFriend
        isSignUpSeamlessFlowIntoPlanningEnabled={isSignUpSeamlessFlowIntoPlanningEnabled}
        renderWrapper={renderWrapper}
      />
    ) : null
  const renderedAppPromo = <AppPromoV2 />

  return (
    <Grid
      paddingH={[3, 5, 3]}
      paddingV={[3, 5, 3]}
      paddingBottom={[3, 5, 3]}
      paddingRight={[3, 5, 3]}
      gap={6}
    >
      {renderMobileLayout(
        renderedOrderSummary,
        renderedBuildYourNextBox,
        renderedAppPromo,
        renderReferAFriend,
        header,
        discountBanner,
      )}
      {renderTabletAndDesktopLayout(
        renderedOrderSummary,
        renderedBuildYourNextBox,
        renderedAppPromo,
        renderReferAFriend,
        header,
        discountBanner,
      )}
    </Grid>
  )
}
